import Phaser from 'phaser';
import { GAME_H, GAME_W, TILE } from '@/core/config';
import { SHOWCASES } from '@/content/ShowcaseScene';
import { attachDebugOverlay, installDebugOverlay } from './DebugOverlay';

export {
  DebugOverlay,
  attachDebugOverlay,
  installDebugOverlay,
  ensureDrawCallCounter,
  isDebugOverlayVisible,
  setDebugOverlayVisible,
  toggleDebugOverlay,
} from './DebugOverlay';
export type { DebugOverlayGlobal, DebugOverlayOptions } from './DebugOverlay';

/** Master-palette swatches (ART_DIRECTION.md) drawn by the debug showcase. */
const SWATCHES = [0xe2c48c, 0x8a5a32, 0x8c2a22, 0x2b3a5c, 0x3c7a3a, 0x8ec95a, 0x1f8f8a, 0x6fd3c8, 0x3a3f57, 0xffb347];

/**
 * Registers `?showcase=debug`: a 32 px grid, palette swatches and a deterministic moving marker with
 * the debug overlay forced on, so critics can check the overlay and the harness in isolation.
 * Call once from main.ts (core request) — it only mutates the SHOWCASES table.
 */
export function registerDebugShowcase(): void {
  SHOWCASES['debug'] = (scene: Phaser.Scene) => {
    scene.cameras.main.setBackgroundColor('#1e2a3a');
    const g = scene.add.graphics();
    g.lineStyle(1, 0x2b3a5c, 1);
    for (let x = 0; x <= GAME_W; x += TILE) g.lineBetween(x, 0, x, GAME_H);
    for (let y = 0; y <= GAME_H; y += TILE) g.lineBetween(0, y, GAME_W, y);
    SWATCHES.forEach((c, i) => { g.fillStyle(c, 1); g.fillRect(64 + i * 48, 232, 32, 32); });
    g.setDepth(0);

    scene.add.text(GAME_W / 2, 64, '调试层展示 · 按 F3 切换', { fontFamily: 'sans-serif', fontSize: '16px', color: '#e2c48c' }).setOrigin(0.5).setDepth(1);
    scene.add.text(GAME_W / 2, 88, 'debug overlay showcase — grid 32px, palette swatches, moving marker', { fontFamily: 'sans-serif', fontSize: '11px', color: '#c9b89a' }).setOrigin(0.5).setDepth(1);

    // Deterministic, time-driven marker (no randomness) so fps / draw-call numbers move.
    const marker = scene.add.rectangle(GAME_W / 2, 200, 32, 48, 0xffb347).setOrigin(0.5, 1);
    const shadow = scene.add.ellipse(GAME_W / 2, 200, 24, 8, 0x05070a, 0.5).setOrigin(0.5);
    const onUpdate = (time: number) => {
      const x = GAME_W / 2 + Math.round(Math.sin(time / 700) * 160);
      const y = 200 + Math.round(Math.cos(time / 1100) * 24);
      marker.setPosition(x, y).setDepth(1000 + y);
      shadow.setPosition(x, y).setDepth(999 + y);
    };
    scene.events.on(Phaser.Scenes.Events.UPDATE, onUpdate);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => scene.events.off(Phaser.Scenes.Events.UPDATE, onUpdate));

    attachDebugOverlay(scene, { visible: true });
  };
}

/**
 * Integrator hook (src/core/wiring.ts convention): `registerShowcase()` registers `?showcase=debug` and,
 * as soon as `window.__nantian.game` exists, installs the F3 / `?debug=1` overlay on the UI scene.
 * Adding `tools` to the MODULES table in wiring.ts is the only core change needed.
 */
export function registerShowcase(): void {
  registerDebugShowcase();
  let tries = 0;
  const tryInstall = () => {
    const game = window.__nantian?.game;
    if (game) { installDebugOverlay(game); return; }
    if (++tries < 600) setTimeout(tryInstall, 50);
  };
  tryInstall();
}

